/**
 * JoinByCodeView — "Join a game" with a code (docs/setup-redesign, section 5).
 *
 * Two states on one screen:
 *   1. Enter the code: code field + Find (same height), and a line saying the
 *      game and its open seats appear here once found.
 *   2. Found: "✓ Found: <game>, hosted by <host>", the compact rules card
 *      (All rules ›) and the seats, each open seat with an identical "Take this
 *      seat". Taking a seat redeems the code (private games) and joins with the
 *      viewer's default colour, then opens the lobby.
 *
 * Rendered by the games list's "Join Private Game" sheet (state 1, with
 * onClose) and by /(app)/join/<code> (straight to 2; invite links land there).
 * A viewer who is already
 * seated, already holds a saved seat, or whose game has started goes straight
 * to the game screen instead.
 */

import React from 'react'
import { View, Text, TextInput, TouchableOpacity, ScrollView, ActivityIndicator, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import { usePreviewGameByCode, useGame, useTakeSeat } from '../../../hooks/useGames'
import { useAuth } from '../../../hooks/useAuth'
import { HttpError } from '../../../lib/api'
import { goBackOr } from '../../../lib/goBack'
import { previewSeatRows, takeSeatJoinRequest, lobbyRole } from '../../../lib/lobbySeats'
import { takeSeatErrorMessage } from '../../../lib/takeSeat'
import { LobbyRulesCard } from './LobbyRulesCard'
import { LobbySeatRow, MiniAction } from './LobbySeatRow'
import { teamForSlot, teamsConfigOf } from '@warcarp/api-types'

export const JOIN_CODE_LENGTH = 4
const FIELD_HEIGHT = 46

/** Digits only, at most JOIN_CODE_LENGTH. */
export function normalizeJoinCode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, JOIN_CODE_LENGTH)
}

/** Message for a failed code lookup. */
export function joinLookupError(err: unknown): string {
  if (err instanceof HttpError && err.status === 410) return 'That game is no longer available.'
  if (err instanceof HttpError && err.status === 404) return "We couldn't find a game with that code. Check it and try again."
  return 'Could not look up that code. Check your connection and try again.'
}

export function JoinByCodeView({ initialCode, onClose }: {
  initialCode?: string
  /** Shown as a sheet: Back closes it instead of navigating. */
  onClose?: () => void
}) {
  const router = useRouter()
  const { user } = useAuth()
  const { width } = useWindowDimensions()
  // A code from the URL is looked up as given (an invalid one shows "not
  // found"); typed codes are digits only.
  const start = (initialCode ?? '').trim()
  const [code, setCode] = React.useState(start)
  const [submitted, setSubmitted] = React.useState<string | null>(start || null)
  const [takeError, setTakeError] = React.useState<string | null>(null)
  const [pendingSlot, setPendingSlot] = React.useState<number | null>(null)
  const preview = usePreviewGameByCode(submitted)
  const found = preview.data && submitted && preview.data.inviteCode === submitted ? preview.data : null
  // Members, invitees with a saved seat and started games belong on the game
  // screen. A private game the viewer can't open yet 403s here; that's fine.
  const { data: fullGame } = useGame(found?.id)
  const takeSeat = useTakeSeat()
  const redirected = React.useRef(false)

  React.useEffect(() => {
    if (!found || redirected.current) return
    const role = fullGame ? lobbyRole(fullGame, user?.id) : null
    if (found.status !== 'LOBBY' || role === 'host' || role === 'member' || role === 'invited') {
      redirected.current = true
      leaveTo(found.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found, fullGame, user?.id])

  /** Open the game: from the sheet, close it and push; from the route, replace. */
  function leaveTo(gameId: string) {
    if (onClose) {
      onClose()
      router.push(`/(app)/games/${gameId}`)
    } else {
      router.replace(`/(app)/games/${gameId}`)
    }
  }

  function find() {
    const next = normalizeJoinCode(code)
    setTakeError(null)
    if (next.length !== JOIN_CODE_LENGTH) return
    setSubmitted(next)
  }

  async function take(slot: number) {
    if (!found || !submitted || pendingSlot != null) return
    setTakeError(null)
    setPendingSlot(slot)
    try {
      const req = takeSeatJoinRequest(found.id, slot, found.players, user?.preferredColorSlot)
      await takeSeat.mutateAsync({ ...req, code: submitted, isPublic: found.isPublic })
      redirected.current = true
      leaveTo(found.id)
    } catch (err) {
      setTakeError(takeSeatErrorMessage(err))
      void preview.refetch()
    } finally {
      setPendingSlot(null)
    }
  }

  const lookupError = submitted && preview.error ? joinLookupError(preview.error) : null
  const canFind = code.length === JOIN_CODE_LENGTH && !(preview.isFetching && code === submitted)
  const seats = found ? previewSeatRows(found) : []
  // Pick seats team lobbies draw each seat in its team's shape.
  const pickSeats = teamsConfigOf(found?.setupConfig)?.assignment === 'manual' ? teamsConfigOf(found?.setupConfig) : undefined
  const seatTeam = (slot: number) => (pickSeats ? teamForSlot(slot, pickSeats.size) : null)
  const openSeats = seats.filter((s) => s.kind === 'open').length
  const columnWidth = Math.min(560, width - 32)

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <View className="flex-row items-center px-4 border-b border-olive gap-3" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => (onClose ? onClose() : goBackOr(router, '/(app)/games'))}
          accessibilityLabel="Back to games"
          accessibilityRole="button"
          className="p-1"
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Text className="text-blood font-typed text-base">← Games</Text>
        </TouchableOpacity>
        <Text className="flex-1 text-ink font-stencil text-lg uppercase" accessibilityRole="header">Join a game</Text>
      </View>
      <ScrollView
        testID="join-code-scroll"
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingVertical: 16, alignItems: 'center' }}
      >
        <View style={{ width: columnWidth, gap: 14 }}>
          <View className="bg-paper border border-olive rounded-xl p-3 gap-2.5">
            <Text className="text-ink-soft text-[10px] font-stencil uppercase tracking-widest">Join a private game with its code</Text>
            <View className="flex-row" style={{ gap: 10 }}>
              <TextInput
                testID="join-code-input"
                value={code}
                onChangeText={(v) => { setCode(normalizeJoinCode(v)); setTakeError(null) }}
                placeholder="Code, e.g. 4821"
                placeholderTextColor="rgba(107, 91, 62, 0.6)"
                keyboardType="number-pad"
                maxLength={JOIN_CODE_LENGTH}
                autoFocus={!submitted}
                returnKeyType="go"
                onSubmitEditing={find}
                accessibilityLabel="4-digit invite code"
                className={`bg-paper-up border-2 border-olive rounded-lg px-3 text-ink ${code ? 'font-stencil text-xl tracking-widest' : 'font-typed text-base'}`}
                style={{ height: FIELD_HEIGHT, flex: 1, minWidth: 0 }}
              />
              <TouchableOpacity
                testID="join-code-find"
                onPress={find}
                disabled={!canFind}
                accessibilityLabel="Find game"
                accessibilityRole="button"
                accessibilityState={{ disabled: !canFind, busy: preview.isFetching }}
                className={`bg-blood rounded-lg items-center justify-center ${canFind ? '' : 'opacity-50'}`}
                style={{ height: FIELD_HEIGHT, width: 110 }}
              >
                {preview.isFetching && code === submitted
                  ? <ActivityIndicator size="small" color="white" />
                  : <Text className="text-paper font-stencil text-sm uppercase tracking-wider">Find</Text>}
              </TouchableOpacity>
            </View>
            {found && (
              <Text testID="join-code-found" className="text-ink font-typed text-sm">
                ✓ Found: {found.name?.trim() || 'Untitled game'}, hosted by {found.host.displayName}
              </Text>
            )}
            {lookupError && <Text testID="join-code-error" className="text-blood font-typed text-sm">{lookupError}</Text>}
          </View>

          {!found && !lookupError && (
            <Text className="text-ink-soft font-typed text-sm text-center px-4 py-6" style={{ lineHeight: 20 }}>
              The host shares the code with you. The game and its open seats appear here once you find it.
            </Text>
          )}

          {found && (
            <>
              <LobbyRulesCard
                mapId={found.mapId}
                mapName={null}
                setupConfig={found.setupConfig}
                playersValue={found.minPlayers === found.maxPlayers ? String(found.maxPlayers) : `${found.minPlayers}–${found.maxPlayers}`}
              />
              <Text className="text-ink-soft text-xs font-stencil uppercase tracking-widest">Pick a seat to join</Text>
              <View testID="join-code-seats" style={{ gap: 8 }}>
                {seats.map((seat) => seat.kind === 'player' ? (
                  <LobbySeatRow key={seat.slot} slot={seat.slot} kind="filled" name={seat.name} meta={seat.meta} colorSlot={seat.colorSlot} team={seatTeam(seat.slot)} />
                ) : (
                  <LobbySeatRow key={seat.slot} slot={seat.slot} kind={seat.kind === 'open' ? 'open' : 'invited'} name={seat.name} meta={seat.meta} team={seatTeam(seat.slot)}>
                    {seat.kind === 'open' ? (
                      <MiniAction label="Take this seat" tone="primary" accessibilityLabel={`Take seat ${seat.slot}`}
                        testID={`take-seat-${seat.slot}`} onPress={() => void take(seat.slot)}
                        loading={pendingSlot === seat.slot} disabled={pendingSlot != null && pendingSlot !== seat.slot} />
                    ) : null}
                  </LobbySeatRow>
                ))}
              </View>
              {openSeats === 0 && <Text className="text-ink-soft font-typed text-sm italic">This game is full. No open seats.</Text>}
              {takeError && <Text testID="join-code-take-error" className="text-blood font-typed text-sm">{takeError}</Text>}
            </>
          )}
        </View>
      </ScrollView>
      <View className="border-t border-olive bg-canvas px-4 py-3">
        <Text className="text-ink-soft font-typed text-xs text-center">
          {found ? 'Taking a seat joins the game.' : 'Public games are in the games list; no code needed.'}
        </Text>
      </View>
    </SafeAreaView>
  )
}
